import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleProtoStatus } from "@kikoff/utils/src/proto";

import { SurveyResponseDetail } from "@overlay/shopping/post_purchase_survey/default/survey/question";
import { RootState } from "@store";
import { track } from "@util/analytics";

import { createLoadableSelector, thunk } from "../utils";

const initialState = {
  question: null as FormattedSurveyData | null,
  answer: null as FormattedOption | null,
  embeddedAnswer: null as FormattedOption | null,
  freeformAnswer: "" as string,
  hasSubmittedMainAnswer: false,
};

export type HdyhauSurveyState = typeof initialState;

// ### Reducers
const hdyhauSurveySlice = createSlice({
  name: "hdyhauSurvey",
  initialState,
  reducers: {
    setQuestion(state, { payload }: PayloadAction<FormattedSurveyData | null>) {
      state.question = payload;
    },
    setAnswer(state, { payload }: PayloadAction<FormattedOption | null>) {
      state.answer = payload;
    },
    setEmbeddedAnswer(
      state,
      { payload }: PayloadAction<FormattedOption | null>
    ) {
      state.embeddedAnswer = payload;
    },
    setFreeformAnswer(state, { payload }: PayloadAction<string | null>) {
      state.freeformAnswer = payload;
    },
    setHasSubmittedMainAnswer(state, { payload }: PayloadAction<boolean>) {
      state.hasSubmittedMainAnswer = payload;
    },
  },
});

const { actions } = hdyhauSurveySlice;
export const {
  setAnswer,
  setEmbeddedAnswer,
  setFreeformAnswer,
  setHasSubmittedMainAnswer,
} = actions;
export default hdyhauSurveySlice.reducer;

export const selectAnswer = () => (state: RootState) =>
  state.hdyhauSurvey.answer as FormattedOption | null;

export const selectEmbeddedAnswer = () => (state: RootState) =>
  state.hdyhauSurvey.embeddedAnswer as FormattedOption | null;

export const selectFreeformAnswer = () => (state: RootState) =>
  state.hdyhauSurvey.freeformAnswer as string | null;

export const selectHasSubmittedMainAnswer = () => (state: RootState) =>
  state.hdyhauSurvey.hasSubmittedMainAnswer;

export const selectQuestion = createLoadableSelector(
  () => (state: RootState) => state.hdyhauSurvey.question,
  {
    loadAction: () => getSurveyQuestions(),
  }
);

export function getSurveyQuestions() {
  return thunk((dispatch) =>
    webRPC.Surveys.getSurveyQuestions({
      key: "acquisition_source_new",
    }).then(
      handleProtoStatus({
        SUCCESS: (surveyData) => {
          const [formattedSurveyData, error] = formatSurveyDataQuestion(
            surveyData?.questions?.[0]
          );
          if (error) {
            track("HDYHAU - Error", { type: error });
          }
          dispatch(actions.setQuestion(formattedSurveyData));
        },
        _DEFAULT() {
          track("HDYHAU - Error", { type: "api" });
        },
      })
    )
  );
}

export async function submitHdyhauResponse(
  question: FormattedSurveyData,
  embeddedAnswer: FormattedOption | null,
  answer: FormattedOption,
  freeformAnswer: string
) {
  await webRPC.Surveys.recordResponse({
    question: question.key,
    response: question.prompt,
    responseDetail: JSON.stringify({
      key: embeddedAnswer?.nextQuestion?.key ?? question.key,
      prompt: question.prompt,
      version: 3,
      answers: [
        {
          key: embeddedAnswer?.key ?? answer?.key,
          value: embeddedAnswer?.value ?? answer?.value,
          freeform: freeformAnswer || undefined,
          source: "web",
        },
      ],
    } as SurveyResponseDetail),
  });
}

export interface FormattedOption {
  type: "radio" | "freeform";
  key: string;
  value: string;
  iconUrl: string;
  description: string;
  placeholder?: string;
  nextQuestion?: FormattedSurveyData | null;
}

export interface FormattedSurveyData {
  prompt: string;
  key: string;
  radioOptions: FormattedOption[];
  freeformOptions: FormattedOption[];
}

function formatSurveyDataQuestion(
  question: web.public_.GetSurveyQuestionsResponse.IQuestion
): [FormattedSurveyData, null] | [null, "no_answers"] {
  if (!question?.answers?.[0]?.radio) {
    return [null, "no_answers"];
  }

  const formattedSurveyData: FormattedSurveyData = {
    prompt: question.prompt,
    key: question.key,
    radioOptions: [],
    freeformOptions: [],
  };

  for (const option of question.answers) {
    const [nextQuestion] = formatSurveyDataQuestion(
      option?.radio?.nextQuestion
    );
    if (option.radio) {
      if (!option?.radio?.key || !option?.radio?.value) {
        continue;
      }
      formattedSurveyData.radioOptions.push({
        type: "radio",
        key: option.radio.key,
        value: option.radio.value,
        iconUrl:
          option?.iconUrl ??
          "https://assets.kikoff.com/public/icons/dashboard/hdyhau_icons/other.svg",
        description: option?.description ?? "",
        nextQuestion: nextQuestion ?? null,
      });
    } else if (option.freeform) {
      if (!option?.freeform?.key || !option?.freeform?.value) {
        continue;
      }
      formattedSurveyData.freeformOptions.push({
        type: "freeform",
        key: option.freeform.key,
        value: option.freeform.value,
        iconUrl:
          option?.iconUrl ??
          "https://assets.kikoff.com/public/icons/dashboard/hdyhau_icons/other.svg",
        description: option?.description ?? "",
        placeholder: option?.freeform?.placeholder ?? "",
        nextQuestion: nextQuestion ?? null,
      });
    }
  }

  formattedSurveyData.radioOptions.sort((a, b) => {
    if (a.key === "acquisition__something_else") {
      return 1;
    }
    if (Math.random() > 0.5) {
      return 1;
    }
    return -1;
  });

  return [formattedSurveyData, null];
}
